import { isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { sdgGoalUrl } from '@/lib/sdg-utils';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { Card, CardContent, CardFooter } from '@worldcoo/ui/components/card';
import { CampaignStatistics } from './statistics-campaign';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { type ReactNode, Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { RingProgressBar } from './ring-progress-bar';
import { Separator } from '@worldcoo/ui/components/separator';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { Image } from '../../-components/image';
import { SdgIcon } from '@/lib/sdg-icons';
import {
	usePortalAuthorQuery,
	usePortalCampaignQuery,
	usePortalCampaignAggregateQuery,
	usePortalMetadataQuery,
} from '@/lib/data-hooks';
import { PortalAnimateNumber } from './portal-animate-number';
import { useCampaignDisplayContext } from './campaign-display-context';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { sdgMessages } from '@/lib/portal-messages';

export function CampaignDetails(props: { campaignId: string }) {
	const { t } = useLingui();
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign, locale, aggregates, onDonate } = displayData;
		return (
			<CampaignDetailsLayout
				progress={
					aggregates &&
					aggregates.funding != null && (
						<>
							{/* Progress ring + "N% Funded": a figure and its label, kept out of page translation —
			    see the container note in `statistics-campaign.tsx`. */}
							<div translate="no" className="flex items-center gap-4 lg:items-end lg:gap-5">
								<RingProgressBar
									radius={40}
									stroke={6}
									// The negative margin claws back the ring's internal padding, which is
									// ~11% of the rendered size on each side, so it changes with the size.
									className="-m-[6px] size-14 lg:-m-[9px] lg:size-20"
									progress={Math.min(aggregates.progress, 100)}
									strokeColor="var(--primary)"
									color="var(--primary)"
								/>
								<div className="flex flex-1 flex-col">
									<span className="inline-flex text-[32px] leading-[100%]">{aggregates.progress}%</span>
									<span className="text-primary text-sm font-semibold lg:text-base">{t`Funded`}</span>
								</div>
							</div>
							<Separator className="hidden lg:block" />
						</>
					)
				}
				statistics={
					aggregates && (
						<div
							translate="no"
							className={cn('bg-background flex gap-16', displayData.metadata.hideCollected && 'justify-center')}
						>
							<div className="flex flex-col font-medium">
								<DonationsIcon className="text-primary mb-2 size-6" />
								<div className="text-xl whitespace-nowrap">{formatNumber(locale, aggregates.donors, 0)}</div>
								<span className="text-primary text-sm font-semibold text-nowrap">{t`Donations`}</span>
							</div>
							{!displayData.metadata.hideCollected && (
								<div className="flex flex-col font-medium">
									<CollectedIcon className="text-primary mb-2 size-6" />
									<div className="text-xl whitespace-nowrap">{formatNumber(locale, aggregates.collected, 2)}€</div>
									<span className="text-primary text-sm font-semibold text-nowrap">
										{aggregates.funding
											? (() => {
													const goal = formatNumber(locale, aggregates.funding, 0);
													return t`Goal of ${goal}` + ' €';
												})()
											: t`Collected`}
									</span>
								</div>
							)}
						</div>
					)
				}
				donateButton={
					campaign.finished ? (
						<Button size="xl" className="mt-auto w-full" disabled>
							{t`Campaign funded`}
						</Button>
					) : (
						<Button size="xl" className="mt-auto w-full" onClick={onDonate}>
							{t`Donate`}
						</Button>
					)
				}
				details={
					<>
						<BeneficiariesCard beneficiaries={campaign.beneficiaries} locale={locale} />
						<SdgGoalsCard sdgs={campaign.sdgs} locale={locale} />
					</>
				}
			/>
		);
	}
	return (
		<Suspense>
			<SuspensedCampaignDetails campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignDetails(props: { campaignId: string }) {
	return (
		<CampaignDetailsLayout
			progress={
				<Suspense fallback={<Skeleton />}>
					<CampaignProgress campaignId={props.campaignId} />
				</Suspense>
			}
			statistics={<CampaignStatistics campaignId={props.campaignId} variant="sm" textEffectDisabled />}
			donateButton={<DonateButton campaignId={props.campaignId} />}
			details={<CampaignNgos campaignId={props.campaignId} />}
		/>
	);
}

function CampaignDetailsLayout(props: {
	progress: ReactNode;
	statistics: ReactNode;
	donateButton: ReactNode;
	details: ReactNode;
}) {
	return (
		<aside className="flex flex-col gap-4 sm:gap-6">
			{/* Below `lg` every figure in this card (funded %, donations, collected) is already in the
			    hero stats row, so the whole card is hidden. From lg up the hero and this sidebar sit
			    side by side and it reads as a summary, so it stays. */}
			<DivWithPrimaryColorVariable className="hidden lg:block">
				<Card className="shadow-4xl border-none">
					<CardContent className="flex flex-col gap-4">
						{props.progress}
						{props.statistics}
					</CardContent>
					<CardFooter className="hidden lg:flex">{props.donateButton}</CardFooter>
				</Card>
			</DivWithPrimaryColorVariable>

			{props.details}
		</aside>
	);
}

function CampaignProgress(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const metadata = usePortalMetadataQuery();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const campaignAggregate =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId] : undefined;
	const funding = campaignAggregate?.funding;
	const progressValue = campaignAggregate?.progress ?? 0;
	// Pass the real figure: `AnimateNumber` owns the first-paint guard.
	const progress = useDebounce(funding == null ? 0 : progressValue, 150);

	if (funding == null || isNotFound(campaign) || isNotFound(metadata)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !campaignAggregate?.finished) {
		return null;
	}

	// The entrance is a FIRST-VISIT effect, same rule as the donations/collected counters: arriving here
	// again by client navigation must not replay it. Ring and number share the one flag so they can never
	// disagree — a full ring beside a number still climbing reads as two different figures.
	const instant = campaign.isPageLoaded;

	return (
		<>
			{/* Progress ring + "N% Funded": a figure and its label, kept out of page translation —
			    see the container note in `statistics-campaign.tsx`. */}
			<div translate="no" className="flex items-center gap-4 lg:items-end lg:gap-5">
				<RingProgressBar
					radius={40}
					stroke={6}
					// The negative margin claws back the ring's internal padding, which is
					// ~11% of the rendered size on each side, so it changes with the size.
					className="-m-[6px] size-14 lg:-m-[9px] lg:size-20"
					progress={Math.min(progress, 100)}
					strokeColor="var(--primary)"
					color="var(--primary)"
					entrance={!instant}
				/>
				<div className="flex flex-1 flex-col">
					<span className="inline-flex text-[32px] leading-[100%]">
						<PortalAnimateNumber locale={language} value={progress} suffix="%" instant={instant} />
					</span>
					<span className="text-primary text-sm font-semibold lg:text-base">{t`Funded`}</span>
				</div>
			</div>

			<Separator className="hidden lg:block" />
		</>
	);
}

function DonateButton(props: { campaignId: string }) {
	const { t } = useLingui();
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (!portalId || isNotFound(campaign)) return null;

	return campaign.data.finished || campaign.data.disabled ? (
		<Button size="xl" className="mt-auto w-full" disabled>
			{t`Campaign funded`}
		</Button>
	) : (
		<Link
			to="/$portalId/$campaignId/donate"
			params={{ portalId, campaignId: campaign.data.slugsByLocale[locale] ?? campaign.data.id }}
			search={(prev) => prev}
			className={cn(buttonVariants({ size: 'xl' }), 'mt-auto w-full')}
		>
			{t`Donate`}
		</Link>
	);
}

/** True when the portal itself is the campaign's only NGO, so naming it again is noise. */
export function useIsSingleNgoPortal(ngos: Array<{ id: string }>) {
	const author = usePortalAuthorQuery();
	if (isNotFound(author)) return false;
	return author.type === 'ngo' && ngos.length === 1 && ngos[0]?.id === author.ngoId;
}

function CampaignNgos(props: { campaignId: string }) {
	const { t } = useLingui();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const isSingleNgo = useIsSingleNgoPortal(isNotFound(campaign) ? [] : campaign.data.ngos);

	if (isNotFound(campaign)) return null;

	// Matches the guard inside `Beneficiaries`: either figure on its own is worth showing.
	const { direct, indirect } = campaign.data.beneficiaries;
	const hasBeneficiaries = direct > 0 || indirect > 0;

	// The card only ever holds these two blocks, so without either one it renders as an empty
	// box (TEC-3644): that is a Worldcoo Plus portal showing its own campaign, where the NGO is
	// the portal itself, with the beneficiaries left blank. The NGO block being desktop-only
	// also means a card carrying just the logos has nothing to show below `lg`.
	const logos = campaign.data.ngos.filter((ngo) => ngo.logo !== undefined);
	const hasNgos = !isSingleNgo && logos.length > 0;

	return (
		<>
			{(hasBeneficiaries || hasNgos) && (
				<DivWithPrimaryColorVariable className={cn('flex flex-col gap-3', !hasBeneficiaries && 'hidden lg:flex')}>
					<Card className="shadow-4xl border-none">
						<CardContent className="flex-gap flex flex-col gap-4">
							<Suspense fallback={<Skeleton />}>
								<Beneficiaries campaignId={props.campaignId} />
							</Suspense>

							{hasNgos && (
								/* Below `lg` the NGOs are shown as inline chips above the campaign title
								   (see CampaignNgosInline), so this block is desktop-only. `lg:contents`
								   keeps its children as direct flex items of the card on desktop. */
								<div className="hidden lg:contents">
									<Separator className="first:hidden" />

									{t`A campaign from: `}
									<ul className="flex flex-wrap items-center justify-evenly gap-3">
										{logos
											// There is not a wide support for toSorted() in the browser
											// oxlint-disable-next-line unicorn/no-array-sort
											.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
											.map((ngo) => (
												<li key={ngo.id}>
													<Image
														className="h-16 w-auto object-contain! lg:m-auto lg:h-24"
														src={ngo.logo!.location}
														objectFit="contain"
														height={96}
														aspectRatio={ngo.logo!.width / ngo.logo!.height}
														alt={ngo.name ?? ''}
													/>
												</li>
											))}
									</ul>
								</div>
							)}
						</CardContent>
					</Card>
				</DivWithPrimaryColorVariable>
			)}

			<SdgGoals campaignId={props.campaignId} />

			{campaign.data.ngos.some((ngo) => ngo.accreditedByLealtad) && (
				<Image
					src="/accredited-by-lealtad.png"
					height={57}
					aspectRatio={128 / 57}
					// TODO: translate for accessibility
					alt="accredited by lealtad"
				/>
			)}
		</>
	);
}

function Beneficiaries(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return;

	// An NGO can fill in one figure and leave the other blank, so each stands on its own.
	// With both blank there is nothing to show, not even the heading.
	const { direct, indirect } = campaign.data.beneficiaries;
	if (direct <= 0 && indirect <= 0) return;

	return (
		<>
			{t`Beneficiaries`}

			<div className="flex items-center justify-center gap-6 lg:justify-start">
				{direct > 0 && (
					<div className="flex items-center gap-2">
						<span className="font-[Helvetica] text-xl tracking-tight">{formatNumber(language, direct, 0)}</span>
						<span className="text-primary text-sm font-medium">{t`direct`}</span>
					</div>
				)}

				{indirect > 0 && (
					<div className="flex items-center gap-2">
						<span className="font-[Helvetica] text-xl tracking-tight">{formatNumber(language, indirect, 0)}</span>
						<span className="text-primary text-sm font-medium">{t`indirect`}</span>
					</div>
				)}
			</div>
		</>
	);
}

function SdgGoals(props: { campaignId: string }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	return <SdgGoalsCard sdgs={campaign.data.sdgs} locale={locale} />;
}

function BeneficiariesCard(props: { beneficiaries: { direct: number; indirect: number }; locale: string }) {
	const { t } = useLingui();
	const hasBeneficiaries = props.beneficiaries.direct > 0 || props.beneficiaries.indirect > 0;
	if (!hasBeneficiaries) return null;

	return (
		<DivWithPrimaryColorVariable className="flex flex-col gap-3">
			<Card className="shadow-4xl border-none">
				<CardContent className="flex flex-col gap-4">
					{t`Beneficiaries`}

					<div className="flex items-center gap-6">
						{props.beneficiaries.direct > 0 && (
							<div className="flex items-center gap-2">
								<span className="font-[Helvetica] text-xl tracking-tight">
									{formatNumber(props.locale, props.beneficiaries.direct, 0)}
								</span>
								<span className="text-primary text-sm font-medium">{t`direct`}</span>
							</div>
						)}

						{props.beneficiaries.indirect > 0 && (
							<div className="flex items-center gap-2">
								<span className="font-[Helvetica] text-xl tracking-tight">
									{formatNumber(props.locale, props.beneficiaries.indirect, 0)}
								</span>
								<span className="text-primary text-sm font-medium">{t`indirect`}</span>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</DivWithPrimaryColorVariable>
	);
}

function SdgGoalsCard(props: { sdgs?: number[]; locale: string }) {
	const { t } = useLingui();
	if (!props.sdgs || props.sdgs.length === 0) return null;

	const title = t`Sustainable Development Goals`;

	return (
		<DivWithPrimaryColorVariable className="flex flex-col gap-3">
			<Card className="shadow-4xl border-none">
				<CardContent className="flex flex-col gap-3">
					<span className="text-sm font-medium">{title}</span>
					<ul className="flex flex-col gap-2" aria-label={title}>
						{props.sdgs.map((sdgNumber) => {
							const name = sdgMessages[sdgNumber] ? t(sdgMessages[sdgNumber]) : '';
							return (
								<li key={sdgNumber}>
									<a
										href={sdgGoalUrl(sdgNumber, props.locale)}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-2 rounded transition-opacity hover:opacity-80"
										aria-label={`${name} — SDG ${sdgNumber}`}
									>
										<SdgIcon goal={sdgNumber} className="size-10 shrink-0 rounded" />
										<span className="text-sm">{name}</span>
									</a>
								</li>
							);
						})}
					</ul>
				</CardContent>
			</Card>
		</DivWithPrimaryColorVariable>
	);
}
